import { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import * as api from "./Api";
import { GameState } from "./Types/Game";
import { PieChart } from "react-minimal-pie-chart";
import { LabelRenderProps } from "react-minimal-pie-chart/types/Label";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

interface Props {
  onBack: () => void;
}

const SecretButton = styled.div`
  opacity: 0;
  width: 10px;
  height: 10px;
  position: absolute;
  top: 0px;
  left: 0px;
`;

const StyledStatistics = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  padding-bottom: 40px;

  h1 {
    font-size: 40px;
  }
`;

const StatisticsGrid = styled.div`
  display: grid;
  grid-auto-flow: row;
  gap: 20px;
`;

const StatisticsBox = styled.div`
  border: 2px dashed white;
  padding: 20px;
  background: black;

  h2 {
    font-size: 24px;
  }

  table {
    margin: 0 auto;
    font-size: large;
    text-align: left;
  }
  th {
    font-weight: bold;
  }
  th,
  td {
    border-bottom: 1px dashed #80805c;
    border-right: 1px dashed #80805c;
    padding: 10px;
  }
`;

const StatisticsNumber = styled.div`
  font-size: large;
  font-weight: bold;
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px 5px;
  gap: 10px;
`;

const ColorSquare = styled.div<{ background: String }>`
  background: ${({ background }) => `${background}`};
  width: 10px;
  height: 10px;
`;

const getDateArray = (start: Date, end: Date) => {
  const arr = [];
  const dt = new Date(start);

  while (dt <= end) {
    arr.push(new Date(dt));
    dt.setDate(dt.getDate() + 1);
  }

  return arr;
};

const getStartOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

interface GameStatistics {
  isTimeLimitEnabled: boolean;
  rules: string;
  ended: string | null;
  created: string;
  players: string[];
  state: GameState;
}
interface PlayerStatistics {
  name?: string;
  numberOfGamesPlayed: number;
  numberOfGamesWon: number;
  numberOfGamesLost: number;
  wonLostRatio: number;
}

const Statistics = ({ onBack }: Props) => {
  const [statistics, setStatistics] = useState<{games: GameStatistics[], visitors: number} | null>(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      const result = await axios.get(`${api.apiUrl}/statistics`);

      setStatistics(result.data);
    };

    fetchStatistics();
  }, []);

  if (!statistics) {
    return <div>Loading</div>;
  }

  let games: Record<string, GameStatistics[]> = {};
  let players: Record<string, PlayerStatistics> = {};
  const { games: gamesStatistics, visitors } = statistics;

  const firstDate = new Date(gamesStatistics[0].created);
  const firstDateStartOfDay = getStartOfDay(firstDate);
  const now = new Date();
  const nowStartOfDay = getStartOfDay(now);
  const dateArray = getDateArray(firstDateStartOfDay, nowStartOfDay);

  gamesStatistics.forEach((game) => {
    const createdStartOfDay = getStartOfDay(new Date(game.created));
    const createdStartOfDayNumber = createdStartOfDay.getTime();
    if (games[createdStartOfDayNumber]) {
      games[createdStartOfDayNumber].push(game);
    } else {
      games[createdStartOfDayNumber] = [game];
    }
    const gameIsWon = game.state === "won";
    game.players.forEach((playerName) => {
      const player = players[playerName];
      if (player) {
        player.numberOfGamesPlayed++;
        player.numberOfGamesWon += gameIsWon ? 1 : 0;
        player.numberOfGamesLost += gameIsWon ? 0 : 1;
        player.wonLostRatio = Math.round(
          (player.numberOfGamesWon / player.numberOfGamesPlayed) * 100
        );
      } else {
        players[playerName] = {
          numberOfGamesPlayed: 1,
          numberOfGamesWon: gameIsWon ? 1 : 0,
          numberOfGamesLost: gameIsWon ? 0 : 1,
          wonLostRatio: gameIsWon ? 100 : 0,
        };
      }
    });
  });

  const prettyData: { date: string; numberOfGamesPlayed: number }[] = [];

  dateArray.forEach((date) => {
    const month = date.getMonth() + 1;
    let prettyMonth = month.toString();
    const prettyDate = `${date.getFullYear()}-${prettyMonth}-${date.getDate()}`;

    prettyData.push({
      date: prettyDate,
      numberOfGamesPlayed: games[date.getTime()]?.length ?? 0,
    });
  });

  const prettyNames: PlayerStatistics[] = [];
  const uniqueNames = Object.keys(players);

  uniqueNames.forEach((name) => {
    prettyNames.push({
      name,
      ...players[name],
    });
  });

  prettyNames.sort((a, b) => b.numberOfGamesPlayed - a.numberOfGamesPlayed);

  const notStarted = gamesStatistics.filter((s) => s.state === "not started").length;
  const started = gamesStatistics.filter((s) => s.state === "started").length;
  const lost = gamesStatistics.filter((s) => s.state === "lost").length;
  const cancelled = gamesStatistics.filter((s) => s.state === "cancelled").length;
  const won = gamesStatistics.filter((s) => s.state === "won").length;

  return (
    <StyledStatistics>
      <SecretButton onClick={onBack} />
      <h1>Statistics</h1>

      <StatisticsGrid>
        <StatisticsBox>
          <h2>Visitors</h2>
          <h3>(since 23/11-2022)</h3>
          <StatisticsNumber>{visitors}</StatisticsNumber>
        </StatisticsBox>

        <StatisticsBox>
          <h2>Games created</h2>
          <h3>(since beginning of time)</h3>
          <StatisticsNumber>{gamesStatistics.length}</StatisticsNumber>
          <LineChart width={800} height={300} data={prettyData}>
            <XAxis dataKey="date" />
            <YAxis />
            <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
            <Line
              type="monotone"
              dataKey="numberOfGamesPlayed"
              stroke="#8884d8"
            />
          </LineChart>
        </StatisticsBox>
        <StatisticsBox style={{ paddingBottom: "150px" }}>
          <h2>Result pie</h2>
          <Legend>
            <ColorSquare background={"#80805c"} />
            Not started: {notStarted}
          </Legend>
          <Legend>
            <ColorSquare background={"#b5b58f"} />
            Not finished: {started}
          </Legend>
          <Legend>
            <ColorSquare background={"maroon"} />
            Lost: {lost}
          </Legend>
          <Legend>
            <ColorSquare background={"hotpink"} />
            Cancelled: {cancelled}
          </Legend>
          <Legend>
            <ColorSquare background={"green"} />
            Won: {won}
          </Legend>
          <PieChart
            labelStyle={{ fontSize: "4px" }}
            label={(labelRenderProps: LabelRenderProps) =>
              `${labelRenderProps.dataEntry.percentage.toFixed()}%`
            }
            data={[
              {
                title: "Not started",
                value: notStarted,
                color: "#80805c",
              },
              {
                title: "Not finished",
                value: started,
                color: "#b5b58f",
              },
              {
                title: "Lost",
                value: lost,
                color: "maroon",
              },
              {
                title: "Cancelled",
                value: cancelled,
                color: "hotpink",
              },
              {
                title: "Won",
                value: won,
                color: "green",
              },
            ]}
          />
        </StatisticsBox>
        <StatisticsBox>
          <h2>Players</h2>

          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Total number of games</th>
                <th>Number of games won</th>
                <th>Number of games lost</th>
                <th>Win/lost ratio</th>
              </tr>
            </thead>
            <tbody>
              {prettyNames.map((nameAndGames) => (
                <tr key={nameAndGames.name}>
                  <td>{nameAndGames.name}</td>
                  <td>{nameAndGames.numberOfGamesPlayed}</td>
                  <td>{nameAndGames.numberOfGamesWon}</td>
                  <td>{nameAndGames.numberOfGamesLost}</td>
                  <td>{`${nameAndGames.wonLostRatio} %`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </StatisticsBox>
      </StatisticsGrid>
    </StyledStatistics>
  );
};

export default Statistics;
